import { QueryFactory, TransportProtocol } from '@lmt/shared-types';
import { AppTooltip } from '@lmt/unicorn-shared/app/components/common/ui-kit/tooltip/AppTooltip';
import { ReactComponent as ClockIcon } from '@lmt/unicorn-shared/app/icons/clock.svg';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { useEffect, useState } from 'react';
import { getFormattedDateTime } from '../../../patients/common';

import styles from './TransporterState.module.scss';
import { addSeconds } from 'date-fns';

interface Props {
  protocol: TransportProtocol;
}

export const TransporterState: React.FC<Props> = React.memo(
  function TransporterState(props) {
    const { protocol } = props;
    const [isConnected, setConnected] = useState<boolean>(false);
    const [tooltip, setTooltip] = useState('');
    const gdtClientQuery = QueryFactory.TransportClientQuery.useGetQuery(
      protocol,
      {
        refetchInterval: 10000,
      },
    );

    // Refresh transport connector every 20 seconds
    const [time, setTime] = useState(Date.now());

    useEffect(() => {
      const interval = setInterval(() => setTime(Date.now()), 20000);
      return () => {
        clearInterval(interval);
      };
    }, [gdtClientQuery.data]);

    useEffect(() => {
      if (gdtClientQuery.data && gdtClientQuery.data[0]) {
        const lastClient = gdtClientQuery.data[0];
        const date = addSeconds(lastClient.lastActivityDateTime, 20);
        setTooltip(
          i18n.t('tooltip', {
            name: lastClient.name,
            date: getFormattedDateTime(lastClient.lastActivityDateTime),
          }),
        );
        if (date > new Date()) {
          setConnected(true);
        } else {
          setConnected(false);
        }
      } else {
        setConnected(false);
        setTooltip(i18n.t('empty'));
      }
    }, [gdtClientQuery.data, time]);

    const i18n = useScopedTranslation('gdt.messages.transporter_state');
    return (
      <div className={styles.container}>
        <div data-connected={isConnected} className={styles.ellipse}></div>
        <div className={styles.text} data-connected={isConnected}>
          {isConnected ? i18n.t('online') : i18n.t('offline')}
        </div>
        {!!tooltip && (
          <AppTooltip title={tooltip}>
            <ClockIcon className={styles.clockIcon} />
          </AppTooltip>
        )}
      </div>
    );
  },
);
